import logo from '@/assets/logo.png';
import { useForm } from 'react-hook-form';
import { ILoginForm, ILoginResponse, IRoleResponse } from '@/interfaces/Auth/auth.interface';
import FormField from '@/components/common/FormField';
import Button from '@/components/common/Button';
import { errorTypes } from '@/utility/validation';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { useEffect } from 'react';
import { useAppDispatch } from '@/redux/store';
import { fetchUserDetails, setAuthenticated, setRole, setUserData } from '@/redux/features/UserSlice';
import { v4 as uuidv4 } from 'uuid';
import { setCookies, showToastNotification } from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { useRoleNavigate } from '@/hooks/useRoleNavigate';
import { requestToken } from '@/firebase';
import { isSupported } from 'firebase/messaging';
import { Link } from 'react-router-dom';

export default function LoginPage() {
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<ILoginForm>();
  const dispatch = useAppDispatch();
  const navigate = useRoleNavigate();
  const randomDeviceId = uuidv4();

  useEffect(() => {
    const getMessagingToken = async () => {
      try {
        const supported = await isSupported();

        if (!supported) {
          return;
        }

        if (Notification.permission === 'granted') {
          await requestToken();
        } else if (Notification.permission === 'default') {
          const permission = await Notification.requestPermission();
          if (permission === 'granted') {
            await requestToken();
          } else {
            localStorage.setItem(`${import.meta.env.VITE_BUSINESS}fcmToken`, '');
            showToastNotification(
              'danger',
              'Please enable notifications to receive updates.',
            );
          }
        } else {
          localStorage.setItem(`${import.meta.env.VITE_BUSINESS}fcmToken`, '');
          showToastNotification(
            'danger',
            'Notifications are blocked. Please enable them in your browser settings to receive updates.',
          );
        }
      } catch (error) {
        console.error('FCM Flow: Error during initialization:', error);
      } finally {
        // setIsFcmLoading(false);
      }
    };
    getMessagingToken();
    getRole();
  }, [])

  const getRole = async () => {
    try {
      const response = await callApi<IRoleResponse>(API_URL + API_ENDPOINT.roles, {
        method: "GET"
      });
      const rolesObject = (response?.data || []).reduce((acc, role) => {
        const key = role.name.replace(/\s+/g, '');
        acc[key] = role;
        return acc;
      }, {} as any);
      dispatch(setRole(rolesObject));
    } catch (error) {
      ThrowError(error);
    }
  }

  const onSubmit = async (data: ILoginForm) => {
    const trimmedData = {
      identifier: data.identifier?.trim(),
      password: data.password?.trim()
    };
    const deviceId =
      localStorage.getItem('deviceId') || '2026_' + randomDeviceId;
    localStorage.setItem('deviceId', deviceId);
    try {
      const response = await callApi<ILoginResponse>(API_URL + API_ENDPOINT.auth + '/login', {
        method: "POST",
        data: { ...trimmedData, deviceId: deviceId, loginType: "web", deviceToken: localStorage.getItem(`${import.meta.env.VITE_BUSINESS}fcmToken`) || null },
      });
      setCookies(
        `${import.meta.env.VITE_BUSINESS}token`,
        response?.data?.accessToken,
      );
      setCookies(`${import.meta.env.VITE_BUSINESS}role`, response?.data?.roleId);
      setCookies(
        `${import.meta.env.VITE_BUSINESS}refresh_token`,
        response?.data?.refreshToken,
      );
      dispatch(setUserData(response?.data));
      await dispatch(fetchUserDetails());
      dispatch(setAuthenticated(true));
      navigate(RoutesLink.dashboard);
      showToastNotification('success', response?.message);
    } catch (error) {
      ThrowError(error);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-full h-full" style={{
          backgroundImage: 'radial-gradient(circle at 25% 25%, hsl(var(--primary)) 2px, transparent 2px), radial-gradient(circle at 75% 75%, hsl(var(--primary)) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }} />
      </div>

      <div className="w-full max-w-md relative z-10">
        <div className="bg-card rounded-xl border shadow-2xl p-8">
          {/* Logo */}
          <div className="flex flex-col items-center mb-8">
            <img src={logo} alt="Yebo Eggs" className="w-24 h-24 mb-3 rounded-full shadow-lg" />
            <h1 className="text-xl font-bold text-foreground">Yebo Eggs</h1>
            <p className="text-xs text-muted-foreground">Store Management System</p>
          </div>

          <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
            {/* {error && (
              <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {error}
              </div>
            )} */}

            <div>
              <FormField
                name="Identifier"
                type="email"
                className="w-full h-10 px-3 rounded-md border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="admin@yeboeggs.co.za"
                {...register('identifier', {
                  ...errorTypes.required('Email'),
                  ...errorTypes.email,
                })}
                requiredIcon={true}
                error={errors.identifier?.message}
              />
            </div>

            <div>
              <FormField
                name="password"
                type="password"
                className="w-full h-10 px-3 rounded-md border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="••••••••"
                {...register('password', {
                  ...errorTypes.required('Password'),
                })}
                requiredIcon={true}
                error={errors.password?.message}
              />
              <div className="flex justify-end mt-1">
                <Link to={RoutesLink.forgotPassword} className="text-xs text-primary hover:underline font-medium">
                  Forgot Password?
                </Link>
              </div>
            </div>

            <Button type="submit" fullWidth isLoading={isSubmitting} loadingText="Signing in...">
              Sign In
            </Button>
          </form>
        </div>
        <p className="text-center text-[10px] text-muted-foreground mt-4">© {new Date().getFullYear()} Yebo Eggs (Pty) Ltd · South Africa</p>
      </div>
    </div>
  );
}
